/** Shared display formatting. */

/**
 * Render a booking slot as a range.
 *
 * The end used to be printed with `toLocaleTimeString()` unconditionally, which
 * silently dropped the date — a 13-day boarding or walk plan (4 Aug → 17 Aug)
 * read as a two-hour appointment. Same-day slots still show the time alone,
 * because repeating the date there is just noise.
 */
export function format_slot(start: string, end: string): string {
  const from = new Date(start);
  const to = new Date(end);
  const same_day = from.toDateString() === to.toDateString();
  return `${from.toLocaleString()} – ${same_day ? to.toLocaleTimeString() : to.toLocaleString()}`;
}

/* ==========================================================================
   The helpers below were copy-pasted into individual pages — `money` and
   `humanize` existed in three files each, `ago` in two, with slightly
   different rounding and casing in each copy. One definition, imported.
   ========================================================================== */

/** Rupees, Indian digit grouping, no decimals. `"1234567"` → `"₹12,34,567"`. */
export function inr(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Rupees keeping paise, for ledger rows where the 50p matters. */
export function inr_exact(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Big counts, abbreviated for stat tiles only — never for money. */
export function compact_number(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (Math.abs(n) < 10_000) return n.toLocaleString('en-IN');
  return new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

/** `AWAITING_VENDOR` → `Awaiting vendor`. */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  const spaced = value.toLowerCase().replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `AWAITING_VENDOR` → `Awaiting Vendor`, for badges that want title case. */
export function title_case(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** `21 Aug 2026` — unambiguous, unlike 21/08 vs 08/21. */
export function date_short(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** `21 Aug 2026, 4:30 pm`. */
export function date_time(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * `4m ago`, `3h ago`, `2d ago`, then an absolute date past a week — "38d ago"
 * is a number an admin has to do arithmetic on.
 */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 0) return date_short(iso);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d ago` : date_short(iso);
}

/** How long ago, as a bare duration — for "open for 6d" style columns. */
export function duration_since(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60_000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** A person's best available label. Never returns an empty string. */
export function person_name(p: {
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}): string {
  const full = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return full || p.name?.trim() || p.email || p.phone || '(no name)';
}

/** Shorten an opaque id for display while keeping it recognisable. */
export function short_id(id: string | null | undefined, keep = 8): string {
  if (!id) return '—';
  return id.length <= keep ? id : `${id.slice(0, keep)}…`;
}
